/** Derive chat engine path from toggle state (multi-engine plan). */

export const RAG_STRATEGY_LEGACY = 'legacy';
export const RAG_STRATEGY_AGENT_LED = 'agent_led';

export const ENGINE_CHAT = 'chat';
export const ENGINE_RAG = 'rag';
export const ENGINE_AGENT = 'agent';

const LS_PREFIX = 'focusml:assistant:';

export function loadToggleOverrides(assistantId) {
  if (!assistantId) return {};
  try {
    const raw = localStorage.getItem(`${LS_PREFIX}${assistantId}:toggles`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveToggleOverrides(assistantId, partial) {
  if (!assistantId) return;
  const prev = loadToggleOverrides(assistantId);
  localStorage.setItem(
    `${LS_PREFIX}${assistantId}:toggles`,
    JSON.stringify({ ...prev, ...partial }),
  );
}

export function resolveInitialToggles(assistant, hasAgentTools, overrides = {}) {
  const chatSettings = assistant?.chat_settings || {};
  const collectionId = assistant?.data_collection_id ?? null;
  const defaultCollectionAttached = collectionId != null
    ? overrides.collectionAttached ?? chatSettings.default_collection_attached ?? true
    : false;
  const toolsEnabled = overrides.toolsEnabled
    ?? chatSettings.default_tools_enabled
    ?? Boolean(hasAgentTools);
  let ragStrategy = overrides.ragStrategy
    ?? chatSettings.default_rag_strategy
    ?? RAG_STRATEGY_LEGACY;
  if (!(defaultCollectionAttached && toolsEnabled)) {
    ragStrategy = RAG_STRATEGY_LEGACY;
  }
  return {
    collectionAttached: defaultCollectionAttached,
    toolsEnabled: Boolean(toolsEnabled && hasAgentTools),
    ragStrategy,
    collectionId: defaultCollectionAttached ? (overrides.collectionId ?? collectionId) : null,
  };
}

/**
 * @returns {{ engine: string, useAgent: boolean, useRag: boolean, ragStrategy: string|null }}
 */
export function deriveEnginePath({ collectionAttached, toolsEnabled, ragStrategy, collectionId }) {
  const hasCollection = collectionAttached && collectionId != null;
  if (!hasCollection && !toolsEnabled) {
    return { engine: ENGINE_CHAT, useAgent: false, useRag: false, ragStrategy: null };
  }
  if (hasCollection && !toolsEnabled) {
    return { engine: ENGINE_RAG, useAgent: false, useRag: true, ragStrategy: RAG_STRATEGY_LEGACY };
  }
  if (!hasCollection && toolsEnabled) {
    return { engine: ENGINE_AGENT, useAgent: true, useRag: false, ragStrategy: null };
  }
  const strategy = ragStrategy === RAG_STRATEGY_AGENT_LED
    ? RAG_STRATEGY_AGENT_LED
    : RAG_STRATEGY_LEGACY;
  return {
    engine: ENGINE_AGENT,
    useAgent: true,
    useRag: strategy === RAG_STRATEGY_LEGACY,
    ragStrategy: strategy,
  };
}

export function buildEngineMetadata(toggles, path) {
  return {
    engine: path.engine,
    collection_attached: Boolean(toggles.collectionAttached && toggles.collectionId),
    tools_enabled: Boolean(toggles.toolsEnabled),
    rag_strategy: path.ragStrategy,
    collection_id: toggles.collectionId ?? null,
  };
}

export function formatEngineStatusLine(toggles, path, collectionName) {
  const parts = [];
  if (toggles.collectionAttached && collectionName) parts.push(collectionName);
  if (toggles.toolsEnabled) parts.push('Tools');
  if (path.ragStrategy === RAG_STRATEGY_LEGACY && toggles.collectionAttached && toggles.toolsEnabled) {
    parts.push('Retrieve first');
  } else if (path.ragStrategy === RAG_STRATEGY_AGENT_LED && toggles.toolsEnabled) {
    parts.push('Agent search');
  }
  return parts.length ? parts.join(' · ') : null;
}
